import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError, api } from '../api/client.js'
import { useSession } from '../api/session.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { Field, Modal, Notice } from '../components/ui.js'
import { formatDateTime } from '../lib/format.js'
import { InviteModal, type Invite } from './CustomerAccess.js'

interface OfficeUser {
  id: string
  name: string
  email: string
  role: 'admin' | 'verwalter'
  active: boolean
  lastSeenAt: string | null
  hasPassword: boolean
}

const ROLE_LABEL: Record<OfficeUser['role'], string> = { admin: 'Administration', verwalter: 'Verwaltung' }

/**
 * Zugänge der Verwaltung: who in the office can sign in. Passwords are set by
 * the person through a link; nobody else ever knows them.
 */
export function OfficeAccess() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { data: session } = useSession()
  const isAdmin = session?.user.role === 'admin'
  const [editing, setEditing] = useState<OfficeUser | 'new' | null>(null)
  const [invite, setInvite] = useState<{ name: string; data: Invite } | null>(null)
  const { data } = useQuery({ queryKey: ['office-users'], queryFn: () => api<OfficeUser[]>('/api/users') })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['office-users'] })
  const fail = (err: unknown) => toast(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.', 'error')

  const newLink = useMutation({
    mutationFn: (user: OfficeUser) => api<Invite>(`/api/users/${user.id}/invite`, { method: 'POST' }),
    onSuccess: (result, user) => setInvite({ name: user.name, data: result }),
    onError: fail,
  })
  const toggle = useMutation({
    mutationFn: (user: OfficeUser) => api(`/api/users/${user.id}`, { method: 'PATCH', body: { active: !user.active } }),
    onSuccess: async (_r, user) => {
      toast(user.active ? `Zugang von ${user.name} gesperrt, alle Anmeldungen beendet.` : `Zugang von ${user.name} wieder aktiv.`)
      await refresh()
    },
    onError: fail,
  })

  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <div className="card-head">
        <div>
          <h2>
            <Icon name="key" size={16} /> Zugänge der Verwaltung
          </h2>
          <div className="muted small">
            Wer sich hier anmelden darf. Das Passwort legt jede Person über einen Link selbst fest. Administration darf zusätzlich
            Administratoren anlegen und ändern.
          </div>
        </div>
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          <Icon name="plus" /> Zugang anlegen
        </button>
      </div>
      {data && data.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th className="nowrap">Rolle</th>
                <th className="nowrap">Status</th>
                <th className="nowrap">Zuletzt da</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((user) => {
                const self = user.id === session?.user.id
                const locked = user.role === 'admin' && !isAdmin
                return (
                  <tr key={user.id} style={user.active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <span className="strong">{user.name}</span>
                      {self && <span className="muted small"> (Sie)</span>}
                      <div className="muted small">{user.email}</div>
                    </td>
                    <td className="nowrap">{ROLE_LABEL[user.role]}</td>
                    <td className="nowrap">
                      {!user.active ? (
                        <span className="badge skipped">gesperrt</span>
                      ) : user.hasPassword ? (
                        <span className="badge done">eingerichtet</span>
                      ) : (
                        <span className="badge warn">Einladung offen</span>
                      )}
                    </td>
                    <td className="nowrap small">{user.lastSeenAt ? formatDateTime(user.lastSeenAt) : <span className="muted">noch nie</span>}</td>
                    <td className="nowrap" style={{ textAlign: 'right' }}>
                      {!locked && (
                        <button type="button" className="btn ghost small" onClick={() => setEditing(user)}>
                          Bearbeiten
                        </button>
                      )}
                      {user.active && !locked && !self && (
                        <button type="button" className="btn ghost small" onClick={() => newLink.mutate(user)}>
                          {user.hasPassword ? 'Passwort zurücksetzen' : 'Neuer Link'}
                        </button>
                      )}
                      {!self && !locked && (
                        <button type="button" className="btn ghost small" onClick={() => toggle.mutate(user)}>
                          {user.active ? 'Sperren' : 'Entsperren'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <OfficeUserModal
          user={editing === 'new' ? null : editing}
          canGrantAdmin={isAdmin}
          self={editing !== 'new' && editing.id === session?.user.id}
          onClose={() => setEditing(null)}
          onSaved={async (result) => {
            setEditing(null)
            await refresh()
            if (result) setInvite(result)
          }}
        />
      )}
      <InviteModal invite={invite} who="die Person" onClose={() => setInvite(null)} />
    </section>
  )
}

function OfficeUserModal({
  user,
  canGrantAdmin,
  self,
  onClose,
  onSaved,
}: {
  user: OfficeUser | null
  canGrantAdmin: boolean
  self: boolean
  onClose: () => void
  onSaved: (invite: { name: string; data: Invite } | null) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (body: { name: string; email?: string; role: string }) =>
      user
        ? api(`/api/users/${user.id}`, { method: 'PATCH', body: { name: body.name, ...(self ? {} : { role: body.role }) } }).then(() => null)
        : api<Invite & { id: string }>('/api/users', { method: 'POST', body }),
    onSuccess: (result, body) => onSaved(result ? { name: body.name, data: result } : null),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <Modal
      open
      title={user ? `Zugang ${user.name}` : 'Zugang für die Verwaltung anlegen'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" form="office-user-form" className="btn" disabled={save.isPending}>
            {user ? 'Speichern' : 'Anlegen und einladen'}
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <form
        id="office-user-form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          save.mutate({
            name: String(form.get('name')),
            email: user ? undefined : String(form.get('email')),
            role: String(form.get('role') ?? user?.role ?? 'verwalter'),
          })
        }}
      >
        <Field label="Name">
          <input name="name" required defaultValue={user?.name} autoFocus />
        </Field>
        {!user && (
          <Field label="E-Mail-Adresse" hint="Damit meldet sich die Person an, und dorthin geht der Link.">
            <input name="email" type="email" required />
          </Field>
        )}
        <Field label="Rolle" hint={self ? 'Die eigene Rolle ändert ein anderer Administrator.' : undefined}>
          <select name="role" defaultValue={user?.role ?? 'verwalter'} disabled={self || !canGrantAdmin}>
            <option value="verwalter">Verwaltung</option>
            <option value="admin">Administration</option>
          </select>
        </Field>
      </form>
    </Modal>
  )
}
